import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import styles from "./Newsletters.module.css";
import { formatNewsletterDate } from "./newsletterUtils";

const emptyPreview = null;
const emptySendAll = null;
const workspaceFilter = createFilterOptions({
  stringify: (option) =>
    `${option?.label || ""} ${option?.owner_email || ""} ${option?.company || ""}`.trim(),
  trim: true,
});

function formatMonthLabel(value) {
  if (!value) return "-";
  const [year, month] = String(value).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

function formatCurrency(amount, currency) {
  if (amount == null || amount === "") return "-";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch (error) {
    return `${currency || ""} ${amount}`.trim();
  }
}

function getWorkspaceCurrency(region) {
  const normalized = String(region ?? "")
    .trim()
    .toUpperCase();
  if (normalized === "1" || normalized === "IN" || normalized === "INR") {
    return "INR";
  }
  return "USD";
}

function WorkspaceMonthlyDigestPage() {
  const [month, setMonth] = useState("");
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");
  const [workspaceId, setWorkspaceId] = useState(null);
  const [previewData, setPreviewData] = useState(emptyPreview);
  const [sendAllResult, setSendAllResult] = useState(emptySendAll);
  const [testEmail, setTestEmail] = useState("");
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    open: false,
    type: "single",
  });
  const [actionLoading, setActionLoading] = useState("");

  const selectedWorkspace = useMemo(
    () =>
      workspaceOptions.find(
        (item) => String(item.value) === String(workspaceId),
      ) || null,
    [workspaceId, workspaceOptions],
  );
  const previewWorkspace = useMemo(
    () =>
      workspaceOptions.find(
        (item) => String(item.value) === String(previewData?.workspace?.id),
      ) || selectedWorkspace,
    [previewData?.workspace?.id, selectedWorkspace, workspaceOptions],
  );
  const displayCurrency = getWorkspaceCurrency(
    previewWorkspace?.region || previewData?.workspace?.region,
  );

  const metrics = previewData?.metrics || {};
  const previewPayload = previewData?.payload || {};
  const upcomingRenewals = Array.isArray(metrics?.upcoming_renewals)
    ? metrics.upcoming_renewals
    : [];
  const sendAllResults = Array.isArray(sendAllResult?.results)
    ? sendAllResult.results
    : [];

  const fetchWorkspaces = async () => {
    setWorkspaceLoading(true);
    setWorkspaceError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/all_workspace`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Unable to load workspaces");
      }
      const rows = Array.isArray(data?.data) ? data.data : [];
      setWorkspaceOptions(
        rows.map((workspace) => ({
          value: workspace.id,
          label:
            workspace.name || workspace.company || `Workspace #${workspace.id}`,
          owner_email: workspace.owner_email || "",
          company: workspace.company || "",
          region:
            workspace.region ??
            workspace.regionId ??
            workspace.curr_region ??
            workspace.country_code,
        })),
      );
    } catch (error) {
      setWorkspaceOptions([]);
      setWorkspaceError(error.message || "Unable to load workspaces");
    } finally {
      setWorkspaceLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runRequest = async ({ endpoint, body, successMessage, loadingKey }) => {
    setActionLoading(loadingKey);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}${endpoint}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Request failed");
      }
      if (successMessage) toast.success(successMessage);
      return data?.data;
    } catch (error) {
      toast.error(error.message || "Request failed");
      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handlePreview = async () => {
    if (!workspaceId || !month) {
      toast.error("Month and workspace are required");
      return;
    }
    const data = await runRequest({
      endpoint: "/admin/monthly-digests/preview",
      body: { workspace_id: workspaceId, month },
      loadingKey: "preview",
    }).catch(() => null);
    if (data) {
      setPreviewData(data);
      setSendAllResult(null);
    }
  };

  const handleSendTest = async () => {
    if (!workspaceId || !month || !testEmail) {
      toast.error("Month, workspace, and test email are required");
      return;
    }
    const data = await runRequest({
      endpoint: "/admin/monthly-digests/send-test",
      body: { workspace_id: workspaceId, month, email: testEmail },
      loadingKey: "send-test",
      successMessage: "Workspace monthly digest test email sent",
    }).catch(() => null);
    if (data) {
      setTestModalOpen(false);
      setPreviewData((prev) =>
        prev
          ? {
              ...prev,
              recipient_email: data?.recipient_email || testEmail,
            }
          : prev,
      );
    }
  };

  const handleSendSingle = async () => {
    if (!workspaceId || !month) {
      toast.error("Month and workspace are required");
      return;
    }
    const data = await runRequest({
      endpoint: "/admin/monthly-digests/send",
      body: { workspace_id: workspaceId, month, force: false },
      loadingKey: "send-single",
      successMessage: "Workspace monthly digest sent",
    }).catch(() => null);
    setConfirmState({ open: false, type: "single" });
    if (data) {
      toast.success(
        `Sent to ${data?.recipient_email || "workspace owner"} (${data?.status || "sent"})`,
      );
    }
  };

  const handleSendAll = async () => {
    if (!month) {
      toast.error("Month is required");
      return;
    }
    const data = await runRequest({
      endpoint: "/admin/monthly-digests/send-all",
      body: { month, force: false },
      loadingKey: "send-all",
      successMessage: "Monthly digests queued for all paid workspaces",
    }).catch(() => null);
    setConfirmState({ open: false, type: "all" });
    if (data) {
      setSendAllResult(data);
      toast.success(
        `Processed ${data?.total || 0} workspaces. Sent: ${data?.sent || 0}, failed: ${data?.failed || 0}`,
      );
    }
  };

  const metricCards = [
    {
      label: "Total Spent This Month",
      value: formatCurrency(metrics?.total_spent_this_month, displayCurrency),
    },
    {
      label: "Overdue Amount",
      value: formatCurrency(metrics?.total_overdue_amount, displayCurrency),
    },
    {
      label: "Overdue Subscriptions",
      value: metrics?.overdue_subscription_count ?? 0,
    },
    {
      label: "Pending Bills",
      value: metrics?.pending_bill_count ?? 0,
    },
    {
      label: "Pending Invoices",
      value: metrics?.pending_invoice_count ?? 0,
    },
    {
      label: "Expiring Subscriptions",
      value: metrics?.expiring_subscription_count ?? 0,
    },
  ];

  return (
    <div className={styles.pageShell}>
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>Operational Email Console</div>
          <h1 className={styles.pageTitle}>Workspace Monthly Digests</h1>
          <p className={styles.pageSubtitle}>
            Preview, test, and send workspace billing digests safely for a
            selected month.
          </p>
        </div>
      </div>

      <div className={styles.digestConsoleGrid}>
        <Paper className={styles.digestControlsCard}>
          <div className={styles.cardHeader}>
            <div>
              <h3>Digest Controls</h3>
              <p>
                Select the month and workspace, then choose the action you need.
              </p>
            </div>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchWorkspaces}
              className={styles.secondaryAction}
              disabled={workspaceLoading}
            >
              {workspaceLoading ? "Refreshing..." : "Refresh Workspaces"}
            </Button>
          </div>

          <div className={styles.digestControlsForm}>
            <TextField
              label="Month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Autocomplete
              options={workspaceOptions}
              loading={workspaceLoading}
              value={selectedWorkspace}
              onChange={(_, value) => setWorkspaceId(value?.value || null)}
              filterOptions={workspaceFilter}
              getOptionLabel={(option) => option?.label || ""}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                <li {...restProps} key={option.value}>
                  <div className={styles.workspaceOption}>
                    <strong>{option.label}</strong>
                    <span>
                      {option.owner_email || option.company || "No owner email"}
                    </span>
                  </div>
                </li>
                );
              }}
              isOptionEqualToValue={(option, value) =>
                String(option?.value) === String(value?.value)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Workspace"
                  placeholder="Search workspace"
                  error={Boolean(workspaceError)}
                  helperText={
                    workspaceError || "Search by workspace name or company"
                  }
                />
              )}
            />

            <div className={styles.selectedWorkspaceCard}>
              <span>Selected Workspace</span>
              <strong>
                {selectedWorkspace?.label || "No workspace selected"}
              </strong>
              <small>
                {selectedWorkspace?.owner_email || "No owner email"}
              </small>
            </div>
          </div>

          <div className={styles.digestActionStack}>
            <Button
              variant="contained"
              startIcon={<VisibilityOutlinedIcon />}
              className={styles.primaryAction}
              disabled={!month || !workspaceId || actionLoading === "preview"}
              onClick={handlePreview}
            >
              {actionLoading === "preview"
                ? "Generating Preview..."
                : "Preview Digest"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendOutlinedIcon />}
              className={styles.secondaryAction}
              disabled={!month || !workspaceId || actionLoading === "send-test"}
              onClick={() => setTestModalOpen(true)}
            >
              Send Test
            </Button>
            <Button
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              className={styles.primaryActionDark}
              disabled={
                !month || !workspaceId || actionLoading === "send-single"
              }
              onClick={() => setConfirmState({ open: true, type: "single" })}
            >
              Send Digest
            </Button>
            <Button
              variant="contained"
              startIcon={<GroupsOutlinedIcon />}
              className={styles.primaryActionDark}
              disabled={!month || actionLoading === "send-all"}
              onClick={() => setConfirmState({ open: true, type: "all" })}
            >
              Send All Paid Workspaces
            </Button>
          </div>
        </Paper>

        <div className={styles.digestPreviewColumn}>
          {previewData ? (
            <>
              <Paper className={styles.previewPanel}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3>
                      {previewData?.workspace?.name ||
                        "Workspace Digest Preview"}
                    </h3>
                    <p>
                      {previewData?.recipient_email || "No recipient"} ·{" "}
                      {formatMonthLabel(previewData?.month)}
                    </p>
                  </div>
                </div>

                <div className={styles.digestMetaStrip}>
                  <div>
                    <span>Subject</span>
                    <strong>{previewPayload?.subject || "-"}</strong>
                  </div>
                  <div>
                    <span>Preheader</span>
                    <strong>{previewPayload?.preheader || "-"}</strong>
                  </div>
                  <div>
                    <span>CTA</span>
                    <strong>{previewPayload?.cta_text || "-"}</strong>
                  </div>
                </div>

                <div className={styles.digestMetricGrid}>
                  {metricCards.map((card, i) => (
                    <div
                      key={`${card.label}-${i}`}
                      className={styles.summaryCard}
                    >
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>

                <div className={styles.digestRenewalsSection}>
                  <div className={styles.cardHeader}>
                    <div>
                      <h3>Upcoming Renewals</h3>
                      <p>Subscriptions due soon for this workspace.</p>
                    </div>
                  </div>
                  {upcomingRenewals.length ? (
                    <TableContainer>
                      <Table className={styles.newsTable}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Marketplace</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {upcomingRenewals.map((item, index) => (
                            <TableRow key={`${item.marketplaceName}-${index}`}>
                              <TableCell>{item.marketplaceName}</TableCell>
                              <TableCell>{item.provider || "-"}</TableCell>
                              <TableCell>
                                {formatNewsletterDate(item.dueDate)}
                              </TableCell>
                              <TableCell>
                                {formatCurrency(
                                  item.amount_default,
                                  displayCurrency,
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <div className={styles.emptyInlineState}>
                      No upcoming renewals for this month.
                    </div>
                  )}
                </div>
              </Paper>

              <Paper className={styles.previewPanel}>
                <div className={styles.previewHeader}>
                  <h3>Final Email HTML</h3>
                  <p>
                    Backend-rendered digest preview for the selected workspace
                    and month.
                  </p>
                </div>
                {previewData?.html ? (
                  <iframe
                    title="Workspace Digest Preview"
                    srcDoc={previewData.html}
                    className={styles.digestIframe}
                  />
                ) : (
                  <div className={styles.emptyInlineState}>
                    No HTML returned for preview.
                  </div>
                )}
              </Paper>
            </>
          ) : (
            <Paper className={styles.previewPanel}>
              <div className={styles.emptyState}>
                <VisibilityOutlinedIcon
                  sx={{ fontSize: 34, mb: 1, color: "#1c496a" }}
                />
                <strong>Preview a workspace digest</strong>
                <p>
                  Pick a month and workspace to load subject, metrics, renewals,
                  and backend HTML.
                </p>
              </div>
            </Paper>
          )}

          {sendAllResult && (
            <Paper className={styles.previewPanel}>
              <div className={styles.cardHeader}>
                <div>
                  <h3>Send All Results</h3>
                  <p>
                    {formatMonthLabel(sendAllResult?.month)} dispatch summary
                  </p>
                </div>
              </div>
              <div className={styles.digestMetricGrid}>
                <div className={styles.summaryCard}>
                  <span>Total</span>
                  <strong>{sendAllResult?.total ?? 0}</strong>
                </div>
                <div className={styles.summaryCard}>
                  <span>Sent</span>
                  <strong>{sendAllResult?.sent ?? 0}</strong>
                </div>
                <div
                  className={`${styles.summaryCard} ${(sendAllResult?.failed || 0) > 0 ? styles.summaryCardWarning : ""}`}
                >
                  <span>Failed</span>
                  <strong>{sendAllResult?.failed ?? 0}</strong>
                </div>
              </div>
              <TableContainer>
                <Table className={styles.newsTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Workspace ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Recipient Email</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sendAllResults.map((row, index) => (
                      <TableRow key={`${row.workspace_id}-${index}`}>
                        <TableCell>{row.workspace_id}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.recipient_email || "-"}</TableCell>
                        <TableCell>{row.error_message || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </div>
      </div>

      <Dialog
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send Test Digest</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Test Email"
            type="email"
            fullWidth
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            helperText="The test email will receive the digest for the selected workspace and month."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestModalOpen(false)}>Close</Button>
          <Button
            onClick={handleSendTest}
            disabled={!testEmail || actionLoading === "send-test"}
            className={styles.primaryAction}
          >
            {actionLoading === "send-test" ? "Sending..." : "Send Test"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmState.open}
        onClose={() => setConfirmState({ open: false, type: "single" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {confirmState.type === "all"
            ? "Send All Paid Workspace Digests"
            : "Send Workspace Digest"}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {confirmState.type === "all"
              ? "This will send monthly digests to all eligible paid workspaces for the selected month."
              : "This will send the monthly digest to the workspace owner email."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmState({ open: false, type: "single" })}
          >
            Cancel
          </Button>
          <Button
            onClick={
              confirmState.type === "all" ? handleSendAll : handleSendSingle
            }
            className={styles.primaryActionDark}
            disabled={
              actionLoading === "send-single" || actionLoading === "send-all"
            }
          >
            {confirmState.type === "all"
              ? actionLoading === "send-all"
                ? "Sending..."
                : "Send All"
              : actionLoading === "send-single"
                ? "Sending..."
                : "Send Digest"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default WorkspaceMonthlyDigestPage;
